import React, { useState } from "react";
import { Button, Input, Checkbox } from "@/components/ds";

function SectionLabel({ children }) {
  return <span className="lf-eyebrow">{children}</span>;
}

function Radio({ label, active, onClick, testId }) {
  return (
    <div data-testid={testId} role="radio" aria-checked={active} tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }} style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", userSelect: "none" }}>
      <span style={{ width: 16, height: 16, flexShrink: 0, borderRadius: "50%", border: `1.5px solid ${active ? "var(--lf-blue-600)" : "var(--border-strong)"}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "all var(--dur-fast)" }}>
        {active && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--lf-blue-600)" }} />}
      </span>
      <span style={{ font: "var(--fw-regular) 13px/1.3 var(--font-sans)", color: "var(--text-secondary)" }}>{label}</span>
    </div>
  );
}

export default function FilterRail({ facets, filters, setFilters, onClear, isMobile }) {
  const [open, setOpen] = useState(false);
  const cnt = (group, key) => (facets?.[group]?.[key] != null ? ` (${facets[group][key]})` : "");
  const categoryKeys = Object.keys(facets?.category || {});
  const fabricKeys = Object.keys(facets?.fabric || {});
  const segTotal = Object.values(facets?.segment || {}).reduce((a, b) => a + b, 0);
  const toggle = (field, val) => setFilters((f) => ({ ...f, [field]: f[field].includes(val) ? f[field].filter((x) => x !== val) : [...f[field], val] }));
  const set = (field, val) => setFilters((f) => ({ ...f, [field]: val }));
  const segActive = (seg) => filters.segments.length === 1 && filters.segments[0] === seg;
  const activeCount = filters.segments.length + filters.categories.length + filters.fabrics.length + ((filters.minPrice || filters.maxPrice) ? 1 : 0);

  const sections = (
    <div style={{ display: "flex", flexDirection: "column", gap: 22, ...(isMobile ? { marginTop: 12, padding: "18px 16px", background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-soft)" } : {}) }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SectionLabel>Segment</SectionLabel>
        <Radio testId="filter-segment-all" label={`All${segTotal ? ` (${segTotal})` : ""}`} active={filters.segments.length === 0} onClick={() => set("segments", [])} />
        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingLeft: 14 }}>
          <Radio testId="filter-segment-women" label={`Women${cnt("segment", "Women")}`} active={segActive("Women")} onClick={() => set("segments", ["Women"])} />
          <Radio testId="filter-segment-men" label={`Men${cnt("segment", "Men")}`} active={segActive("Men")} onClick={() => set("segments", ["Men"])} />
        </div>
      </div>

      {categoryKeys.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <SectionLabel>Category</SectionLabel>
          {categoryKeys.map((c) => (
            <Checkbox key={c} data-testid={`filter-category-${c}`} label={`${c}${cnt("category", c)}`} checked={filters.categories.includes(c)} onChange={() => toggle("categories", c)} />
          ))}
        </div>
      )}

      {fabricKeys.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <SectionLabel>Fabric</SectionLabel>
          {fabricKeys.map((f) => (
            <Checkbox key={f} data-testid={`filter-fabric-${f}`} label={`${f}${cnt("fabric", f)}`} checked={filters.fabrics.includes(f)} onChange={() => toggle("fabrics", f)} />
          ))}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SectionLabel>Price per piece</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 8 }}>
          <Input data-testid="filter-min-price" size="sm" type="number" placeholder="Min" value={filters.minPrice} onChange={(e) => set("minPrice", e.target.value)} />
          <Input data-testid="filter-max-price" size="sm" type="number" placeholder="Max" value={filters.maxPrice} onChange={(e) => set("maxPrice", e.target.value)} />
        </div>
      </div>

      <Button data-testid="clear-filters-btn" variant="ghost" size="sm" fullWidth onClick={onClear}>Clear all filters</Button>
    </div>
  );

  return (
    <aside
      data-testid="filter-rail"
      style={{ display: "flex", flexDirection: "column", gap: 0, padding: isMobile ? 0 : "22px 20px", background: isMobile ? "transparent" : "var(--surface-card)", border: isMobile ? "none" : "1px solid var(--border-default)", borderRadius: "var(--radius-xl)", boxShadow: isMobile ? "none" : "var(--shadow-soft)", position: isMobile ? "static" : "sticky", top: 84 }}
    >
      {isMobile && (
        <button data-testid="filter-toggle" onClick={() => setOpen((o) => !o)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "12px 16px", borderRadius: "var(--radius-lg)", border: "1px solid var(--border-default)", background: "var(--surface-card)", cursor: "pointer", font: "var(--fw-semibold) 14px/1 var(--font-sans)", color: "var(--text-primary)", boxShadow: "var(--shadow-soft)" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
            Filters{activeCount ? ` (${activeCount})` : ""}
          </span>
          <svg width="16" height="16" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform var(--dur-fast)" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
        </button>
      )}
      {(!isMobile || open) && sections}
    </aside>
  );
}
